import React, { useMemo } from 'react';

import { ButtonSize, PrimaryButton, SecondaryButton } from '@components/Button/variants';
import { useSubscribeToNewsletter, useUnsubscribeFromNewsletter } from '@hooks';
import useEventTracking from '@hooks/useEventTracking';
import { highlight_path, subscribe_promotion_path, upgrade_subscription_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { Newsletter } from '@utils/backend-api/newsletters';
import { parameterize } from '@utils/formatting';

interface NewsletterSignupButtonProps {
  buttonSize: ButtonSize;
  canSubscribe: boolean;
  currentUserAccessLevel: number;
  isSubscribed: boolean;
  newsletter: Newsletter;
}

const NewsletterSignupButton = ({
  buttonSize = 'big',
  canSubscribe,
  currentUserAccessLevel,
  isSubscribed,
  newsletter,
}: NewsletterSignupButtonProps) => {
  const { trackEvent } = useEventTracking();
  const { mutate: subscribeToNewsletter, isLoading: isFollowNewsletterLoading } =
    useSubscribeToNewsletter();
  const { mutate: unsubscribeFromNewsletter, isLoading: isUnFollowNewsletterLoading } =
    useUnsubscribeFromNewsletter();

  const handleClickTracking = () => {
    const clickTarget = isSubscribed
      ? ClickTarget.UnsubscribeNewsletter
      : ClickTarget.SubscribeNewsletter;
    trackEvent(click({ clickTarget: clickTarget }));
  };

  const {
    buttonText,
    buttonOnClick,
    buttonHref,
  }: { buttonText: string; buttonOnClick?: () => void; buttonHref?: string } = useMemo(() => {
    if (isSubscribed) {
      // User who already subscribes to this newsletter
      return {
        buttonText: 'You are subscribed',
        buttonOnClick: () => unsubscribeFromNewsletter({ newsletter_id: newsletter.id }),
      };
    } else if (canSubscribe) {
      // User who has enough access for this newsletter
      return {
        buttonText: `Subscribe to ${newsletter.name}`,
        buttonOnClick: () => subscribeToNewsletter({ newsletter_id: newsletter.id }),
      };
    } else if (currentUserAccessLevel > 0) {
      // TI subscriber who doesn't have enough access to this newsletter (e.g. pro)
      return {
        buttonText: `Upgrade to subscribe to ${newsletter.name}`,
        buttonHref: upgrade_subscription_path(),
      };
    } else {
      // Lead who doesn't have enough access to this newsletter (e.g. pro)
      return {
        buttonText: 'Subscribe',
        buttonHref: subscribe_promotion_path({
          offer: newsletter.offerCode || 'yearly',
          redirect_to: highlight_path(parameterize(newsletter.name)),
        }),
      };
    }
  }, [
    currentUserAccessLevel,
    isSubscribed,
    canSubscribe,
    newsletter,
    subscribeToNewsletter,
    unsubscribeFromNewsletter,
  ]);
  const isLoading = isUnFollowNewsletterLoading || isFollowNewsletterLoading;
  const ButtonComponent = isSubscribed ? SecondaryButton : PrimaryButton;

  return (
    <ButtonComponent
      as={buttonOnClick ? 'button' : 'link'}
      onClick={buttonOnClick}
      href={buttonHref}
      data-test={isSubscribed ? 'unsub-btn' : 'sub-btn'}
      disabled={isLoading}
      isLoading={isLoading}
      size={buttonSize}
      onClickCapture={handleClickTracking}
    >
      {buttonText}
    </ButtonComponent>
  );
};

export default NewsletterSignupButton;
